import React, { useState } from 'react';
import TextInput from '../../components/Inputs/Text';
import Button from '../../components/Button';
import { useCreateServerWalletMutation, useGetServerWalletQuery } from '../../redux/api/authorized/serverWallets';

const ServerWallets = () => {
  const [purpose, setPurpose] = useState('INTERNAL');
  const [chainType, setChainType] = useState('solana');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [queryName, setQueryName] = useState('');
  const [queryPurpose, setQueryPurpose] = useState('INTERNAL');
  const [queryChainType, setQueryChainType] = useState('solana');

  const [lastResponse, setLastResponse] = useState(null);
  const [lastError, setLastError] = useState(null);

  const [createServerWallet, { isLoading: isCreating }] = useCreateServerWalletMutation();

  const {
    data: serverWallet,
    refetch: refetchServerWallet,
    isFetching: isFetchingWallet,
  } = useGetServerWalletQuery(
    {
      name: queryName || undefined,
      purpose: !queryName ? queryPurpose : undefined,
      chainType: queryChainType,
    },
    {
      skip: !queryName && !queryPurpose,
    }
  );

  const handleCreate = async () => {
    setLastError(null);
    setLastResponse(null);

    if (!purpose || !name) {
      setLastError({ message: 'Purpose and name are required' });
      return;
    }

    try {
      const result = await createServerWallet({
        purpose,
        chainType,
        name,
        description: description || undefined,
      });

      if (result.error) {
        setLastError({
          message: 'API Error',
          error: result.error.data || result.error.message || 'Unknown error',
          timestamp: new Date().toISOString(),
        });
      } else {
        setLastResponse({
          message: 'Server wallet created successfully',
          data: result.data,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (err) {
      setLastError({
        message: 'Unexpected error',
        error: err.message || 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleFetch = async () => {
    setLastError(null);
    setLastResponse(null);

    if (!queryName && !queryPurpose) {
      setLastError({ message: 'Provide a name or a purpose to fetch a server wallet' });
      return;
    }

    try {
      const result = await refetchServerWallet();

      if (result.error) {
        setLastError({
          message: 'API Error',
          error: result.error.data || result.error.message || 'Unknown error',
          timestamp: new Date().toISOString(),
        });
      } else {
        setLastResponse({
          message: 'Server wallet fetched successfully',
          data: result.data,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (err) {
      setLastError({
        message: 'Unexpected error',
        error: err.message || 'Unknown error',
        timestamp: new Date().toISOString(),
      });
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Admin Server Wallets</h1>

      {/* Create server wallet */}
      <div style={{ marginTop: '24px', marginBottom: '32px' }}>
        <h2>Create Server Wallet</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Purpose</label>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              style={{ width: '100%', padding: 8 }}
            >
              <option value="INTERNAL">INTERNAL</option>
              <option value="PARTNER">PARTNER</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Chain Type</label>
            <select
              value={chainType}
              onChange={(e) => setChainType(e.target.value)}
              style={{ width: '100%', padding: 8 }}
            >
              <option value="solana">solana</option>
              <option value="ethereum">ethereum</option>
            </select>
          </div>

          <TextInput
            label="Name"
            placeholder="Unique wallet name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextInput
            label="Description (optional)"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div style={{ marginTop: 8 }}>
            <Button
              onClick={handleCreate}
              loading={isCreating}
              fullWidth
            >
              Create Server Wallet
            </Button>
          </div>
        </div>
      </div>

      {/* Fetch server wallet */}
      <div style={{ marginBottom: '32px' }}>
        <h2>Get Server Wallet</h2>
        <p style={{ fontSize: 12, color: '#666' }}>
          Fetch a server wallet by <strong>name</strong>, or by <strong>purpose + chain type</strong> if no name is provided.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
          <TextInput
            label="Name (optional)"
            placeholder="Server wallet name"
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
          />

          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Purpose (used if no name)</label>
            <select
              value={queryPurpose}
              onChange={(e) => setQueryPurpose(e.target.value)}
              style={{ width: '100%', padding: 8 }}
            >
              <option value="INTERNAL">INTERNAL</option>
              <option value="PARTNER">PARTNER</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Chain Type</label>
            <select
              value={queryChainType}
              onChange={(e) => setQueryChainType(e.target.value)}
              style={{ width: '100%', padding: 8 }}
            >
              <option value="solana">solana</option>
              <option value="ethereum">ethereum</option>
            </select>
          </div>

          <div style={{ marginTop: 8 }}>
            <Button
              onClick={handleFetch}
              loading={isFetchingWallet}
              fullWidth
              variant="secondary"
            >
              Get Server Wallet
            </Button>
          </div>
        </div>
      </div>

      {/* Result / error display */}
      <div style={{ marginBottom: 24 }}>
        <h3>Last Operation</h3>

        {lastResponse && (
          <div style={{ padding: 12, borderRadius: 4, backgroundColor: '#d4edda', marginBottom: 12 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{lastResponse.message}</p>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              Timestamp: {lastResponse.timestamp}
            </p>
            <pre
              style={{
                backgroundColor: '#f8f9fa',
                padding: 8,
                borderRadius: 4,
                fontSize: 12,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(lastResponse.data, null, 2)}
            </pre>
          </div>
        )}

        {lastError && (
          <div style={{ padding: 12, borderRadius: 4, backgroundColor: '#f8d7da', marginBottom: 12 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{lastError.message}</p>
            {lastError.timestamp && (
              <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Timestamp: {lastError.timestamp}
              </p>
            )}
            {lastError.error && (
              <pre
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: 8,
                  borderRadius: 4,
                  fontSize: 12,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(lastError.error, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>

      {/* Current fetched wallet data */}
      <div>
        <h3>Current Server Wallet Data</h3>
        <pre
          style={{
            backgroundColor: '#f8f9fa',
            padding: 8,
            borderRadius: 4,
            fontSize: 12,
            minHeight: 80,
            overflow: 'auto',
          }}
        >
          {serverWallet ? JSON.stringify(serverWallet, null, 2) : 'No server wallet data (run a query above)'}
        </pre>
      </div>
    </div>
  );
};

export default ServerWallets;
